import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { useStore } from 'zustand'
import { toastr } from 'react-redux-toastr'
import { ArrowLeft, Archive, ArchiveRestore, MoreVertical, Pencil, Settings, Trash2, Lock, Upload, Layers } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import * as Mutation from '../../../api/gql/mutation'
import { useRouter } from '../../../hooks'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ScheduleDetailSkeleton from './ScheduleDetailSkeleton'
import ActivityPanel from './ActivityPanel'
import GanttEditingGroup from './GanttEditingGroup'
import GanttToolbar from './GanttToolbar'
import GanttView, { type GanttViewHandle } from './GanttView'
import ShortcutsDialog from './ShortcutsDialog'
import { GanttOpsContext, type GanttOps } from './gantt-ops'
import ScheduleStatusBadge from '../components/ScheduleStatusBadge'
import ScheduleFormDialog from '../components/ScheduleFormDialog'
import ScheduleSettingsDialog from '../components/ScheduleSettingsDialog'
import ScheduleImportDialog from '../components/ScheduleImportDialog'
import ConfirmDialog from '../components/ConfirmDialog'
import BaselinesDialog from '../components/BaselinesDialog'
import ActivityLogDialog from '../components/ActivityLogDialog'
import HealthCounts from '../components/HealthCounts'
import { useScheduleDetail } from '../hooks/useScheduleDetail'
import { createGanttStore, GanttStoreContext } from '../store/ganttStore'
import { formatIsoDate } from '../lib/format'
import { describeScheduleError } from '../lib/errors'

const ScheduleDetail: React.FC = () => {
  const { t } = useTranslation()
  const { match, history, location } = useRouter()
  const { projectId, scheduleId } = match.params as { projectId: string; scheduleId: string }
  const { canCreate, canEdit, canDelete, canAdmin, canExport } = useModuleActions('SCHEDULE', projectId)
  const { detail, loading, error, refetch, tree, byId, calendarsById, externalById } = useScheduleDetail(scheduleId)

  const store = useMemo(() => createGanttStore(scheduleId), [scheduleId])
  const ganttRef = useRef<GanttViewHandle>(null)
  // `ScheduleDetail` creates `store` itself, so it sits *above* `GanttStoreContext.Provider` in the
  // tree it returns below — it cannot consume its own Provider via `useGanttStore`. Reading straight
  // off the `StoreApi` with zustand's own `useStore` is the same pattern used for every other
  // imperative access to `store` in this component (`store.getState()...`), just reactive.
  const panelOpen = useStore(store, (s) => s.panelOpen)

  const [editOpen, setEditOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [baselinesOpen, setBaselinesOpen] = useState(false)
  const [logOpen, setLogOpen] = useState<{ open: boolean; activityId?: string }>({ open: false })
  // Task 3 (keyboard row entry): the shortcuts help dialog, opened from the toolbar's Keyboard
  // button or the `?` key (`GanttView` → `useGanttKeyboard`'s `onShowShortcuts`).
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  // Task 8 (print/PDF): owned here so the toolbar button (a sibling of `GanttView`) can open the
  // dialog `GanttView` renders — it is the only place that holds the laid-out rows/range the
  // printable view needs.
  const [printOpen, setPrintOpen] = useState(false)
  // Task 16/17: `GanttView` reports its `GanttOps` bundle up here so consumers that render as
  // siblings, not descendants, of `GanttView` — the toolbar's `GanttEditingGroup` and the activity
  // panel — can reach it through `GanttOpsContext`.
  const [ops, setOps] = useState<GanttOps | null>(null)
  const onOps = useCallback((next: GanttOps | null) => setOps(next), [])
  const [pending, setPending] = useState<'archive' | 'restore' | 'delete' | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [updateSchedule] = useMutation(Mutation.UPDATE_SCHEDULE)
  const [deleteSchedule] = useMutation(Mutation.DELETE_SCHEDULE)

  const schedule = detail?.schedule
  const readOnly = schedule?.status === 'ARCHIVED'
  const hasBaseline = !!schedule?.activeBaselineId

  // ?activity=<id> deep link → focus once the data is in
  const deepLink = useMemo(() => new URLSearchParams(location.search).get('activity'), [location.search])
  useEffect(() => { if (deepLink && tree && tree.byId.has(deepLink)) ganttRef.current?.focusActivity(deepLink, { openPanel: true }) }, [deepLink, tree])

  const runPending = async () => {
    if (!pending || !schedule) return
    setSubmitting(true)
    try {
      if (pending === 'delete') { await deleteSchedule({ variables: { id: schedule.id } }); toastr.success('', t('Schedule_deleted')); history.push(`/project/${projectId}/schedule`); return }
      await updateSchedule({ variables: { id: schedule.id, input: { status: pending === 'archive' ? 'ARCHIVED' : 'ACTIVE' } } })
      toastr.success('', t(pending === 'archive' ? 'Schedule_archived' : 'Schedule_restored'))
      setPending(null)
      refetch().catch(() => undefined) // review fix (Minor #12): avoid an unhandled rejection on a transient network failure
    } catch (e: any) { toastr.error(t('Common_error'), describeScheduleError(e.message, t)) } finally { setSubmitting(false) }
  }
  const doRefresh = async () => { setRefreshing(true); try { await refetch().catch(() => undefined) } finally { setRefreshing(false) } }

  if (loading && !detail) return <ScheduleDetailSkeleton />
  if (error || !detail || !schedule || !tree) {
    return (
      <Card><CardContent className="py-12 text-center space-y-3">
        <p className="text-muted-foreground">{error ? describeScheduleError(error.message, t) : t('Schedule_not_found')}</p>
        <Button variant="outline" onClick={() => history.push(`/project/${projectId}/schedule`)}><ArrowLeft className="h-4 w-4" />{t('Common_back')}</Button>
      </CardContent></Card>
    )
  }
  const s = schedule.stats

  return (
    <GanttStoreContext.Provider value={store}>
      <div className="flex flex-col h-[calc(100vh-135px)] min-h-0">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => history.push(`/project/${projectId}/schedule`)}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-lg font-semibold truncate max-w-[40vw]">{schedule.name}</h2>
          <ScheduleStatusBadge status={schedule.status} />
          <span className="text-sm text-muted-foreground tabular-nums">{formatIsoDate(t, schedule.start)} – {formatIsoDate(t, schedule.finish)}</span>
          <div className="flex items-center gap-3 text-sm tabular-nums">
            <span>{t('Schedule_activity_count', { count: s.total })}</span>
            <span className="text-destructive">{s.critical} {t('Schedule_critical').toLowerCase()}</span>
            {/* review fix (Minor #11): reuse `HealthCounts`'s on-track/at-risk/delayed colours instead of a second hand-rolled copy */}
            <HealthCounts stats={s} />
          </div>
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && !readOnly && <DropdownMenuItem onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4 mr-2" />{t('Common_edit')}</DropdownMenuItem>}
                {canAdmin && <DropdownMenuItem onClick={() => setBaselinesOpen(true)}><Layers className="h-4 w-4 mr-2" />{t('Schedule_baselines')}</DropdownMenuItem>}
                {canAdmin && <DropdownMenuItem onClick={() => setSettingsOpen(true)}><Settings className="h-4 w-4 mr-2" />{t('Schedule_settings_title')}</DropdownMenuItem>}
                {canCreate && <DropdownMenuItem onClick={() => setImportOpen(true)}><Upload className="h-4 w-4 mr-2" />{t('Schedule_import')}</DropdownMenuItem>}
                {canAdmin && !readOnly && <DropdownMenuItem onClick={() => setPending('archive')}><Archive className="h-4 w-4 mr-2" />{t('Schedule_action_archive')}</DropdownMenuItem>}
                {canAdmin && readOnly && <DropdownMenuItem onClick={() => setPending('restore')}><ArchiveRestore className="h-4 w-4 mr-2" />{t('Schedule_action_restore')}</DropdownMenuItem>}
                {canDelete && (<><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive" onClick={() => setPending('delete')}><Trash2 className="h-4 w-4 mr-2" />{t('Common_delete')}</DropdownMenuItem></>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {readOnly && <Alert className="mt-2"><Lock className="h-4 w-4" /><AlertDescription>{t('Schedule_archived_banner')}</AlertDescription></Alert>}

        {/* Task 17: the toolbar's `GanttEditingGroup` and the activity panel both read `GanttOps`
            (`run`, the predecessor/successor maps, `focusActivity`) reported by `GanttView` below —
            neither is a descendant of it, so they can't reach it any other way. */}
        <GanttOpsContext.Provider value={ops}>
          <GanttToolbar
            hasBaseline={hasBaseline} readOnly={readOnly || !canEdit}
            onToday={() => ganttRef.current?.scrollToToday()}
            onCollapseAll={() => store.getState().setCollapsed(tree.order.filter((n) => n.children.length > 0).map((n) => n.activity.id), true)}
            onSearchNext={() => ganttRef.current?.searchNext()}
            onRefresh={doRefresh} refreshing={refreshing}
            onOpenLog={() => setLogOpen({ open: true })}
            onOpenShortcuts={() => setShortcutsOpen(true)}
            // An empty schedule the viewer cannot edit never mounts `GanttView`, and the print
            // dialog lives inside it — the button would open a dialog that is not there.
            onOpenPrint={canExport && detail.activities.length > 0 ? () => setPrintOpen(true) : undefined}
            editingSlot={canEdit && !readOnly && ops ? <GanttEditingGroup ops={ops.rowOps} /> : undefined}
          />

          <div className="flex-1 min-h-0 flex">
            {/* Task 16: an editable empty schedule still mounts `GanttView` (empty grid + toolbar Add
                group) so it can be built from scratch with Insert; only a genuinely dead end — no
                activities and nothing the viewer can do about it — falls back to the message card. */}
            {detail.activities.length === 0 && !(canEdit && !readOnly) ? (
              <Card className="flex-1"><CardContent className="py-12 text-center space-y-3">
                <p className="text-muted-foreground">{t('Schedule_empty_schedule')}</p>
                {canCreate && !readOnly && <Button variant="outline" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" />{t('Schedule_import')}</Button>}
              </CardContent></Card>
            ) : (
              <GanttView ref={ganttRef} detail={detail} tree={tree} byId={byId} calendarsById={calendarsById} externalById={externalById} canEdit={canEdit && !readOnly} canCreate={canCreate} projectId={projectId} refetchDetail={refetch}
                onFocusChange={(id) => { const p = new URLSearchParams(location.search); if (id) p.set('activity', id); else p.delete('activity'); history.replace({ pathname: location.pathname, search: p.toString() ? `?${p}` : '' }) }}
                onOpenLog={(activityId) => setLogOpen({ open: true, activityId })} onOps={onOps} onShowShortcuts={() => setShortcutsOpen(true)}
                printOpen={printOpen} onPrintOpenChange={setPrintOpen} />
            )}
            {/* Review fix (Important #1): `ops` reaches this component one commit after `panelOpen`
                can already be true (the `?activity=` deep link sets `panelOpen` synchronously inside
                `GanttView`'s `focusActivity`, while `onOps` fires from a separate effect one render
                later) — `ActivityPanel`'s sections all call `useGanttOps()`, which throws outside its
                provider, so guard on both. */}
            {panelOpen && ops && (
              <ActivityPanel
                detail={detail} byId={byId} externalById={externalById} calendarsById={calendarsById}
                canEdit={canEdit && !readOnly} projectId={projectId} scheduleId={schedule.id}
                onOpenLog={(activityId) => setLogOpen({ open: true, activityId })}
              />
            )}
          </div>
        </GanttOpsContext.Provider>
      </div>

      <ScheduleFormDialog open={editOpen} onOpenChange={setEditOpen} projectId={projectId} schedule={schedule} onSaved={() => { refetch().catch(() => undefined) }} />
      {settingsOpen && <ScheduleSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} projectId={projectId} />}
      {importOpen && <ScheduleImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={projectId} />}
      {baselinesOpen && <BaselinesDialog open={baselinesOpen} onOpenChange={setBaselinesOpen} scheduleId={schedule.id} baselines={detail.baselines} activeBaselineId={schedule.activeBaselineId} readOnly={readOnly} onChanged={() => { refetch().catch(() => undefined) }} />}
      {logOpen.open && <ActivityLogDialog open={logOpen.open} onOpenChange={(o) => setLogOpen({ open: o })} scheduleId={schedule.id} activityId={logOpen.activityId} onFocusActivity={(id) => ganttRef.current?.focusActivity(id, { openPanel: true })} />}
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <ConfirmDialog open={!!pending} title={pending ? t(`Schedule_confirm_${pending}_title`) : ''} description={pending ? t(`Schedule_confirm_${pending}_body`, { name: schedule.name, count: schedule.activityCount }) : undefined}
        confirmLabel={pending ? t(`Schedule_confirm_${pending}_action`) : ''} variant={pending === 'delete' ? 'destructive' : 'default'} submitting={submitting} onCancel={() => setPending(null)} onConfirm={runPending} />
    </GanttStoreContext.Provider>
  )
}
export default ScheduleDetail
